import { LockedHint } from "@/components/predictions/LockedHint";
import { uiText } from "@/lib/i18n/uiText";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import MatchPreview from "@/pages/MatchPreview";
import { MainLayout } from "@/components/layout/MainLayout";
import { socialMeta } from "@/lib/routeSocial";
import { getPublicMatchSummary } from "@/lib/publicPicks.functions";
import { GATES_SUSPENDED } from "@/lib/accessMode";
import { sportsEventLd } from "@/lib/picksJsonLd";

const searchSchema = z.object({
  home: z.string().optional(),
  away: z.string().optional(),
  league: z.string().optional(),
}).passthrough();

const summaryQuery = (home: string, away: string) =>
  queryOptions({
    queryKey: ["public-match-summary", home, away],
    queryFn: () => (home && away ? getPublicMatchSummary({ data: { home, away } }) : Promise.resolve(null)),
    staleTime: 5 * 60_000,
  });

export const Route = createFileRoute("/match-preview")({
  validateSearch: (s) => searchSchema.parse(s),
  loaderDeps: ({ search }) => ({ home: search.home ?? "", away: search.away ?? "" }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(summaryQuery(deps.home, deps.away)),
  head: ({ match, loaderData }) => {
    const s = match.search as { home?: string; away?: string };
    const named = s.home && s.away;
    return {
      meta: socialMeta({
        seo: { topics: ["fixtures", "footballPredictions"] },
        title: named ? `${s.home} vs ${s.away} Prediction, Form & Odds | Statlign` : "Match Preview & Prediction Builder | Statlign",
        description: named
          ? `${s.home} vs ${s.away}: model pick, odds, confidence, last-10 results, formations and the strategy signals behind the Statlign prediction.`
          : "Build a full match preview: model pick, odds, confidence, form and the strategy signals behind every Statlign prediction.",
        ogType: "article",
      }),
      scripts: loaderData?.pick
        ? [{ type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", ...sportsEventLd(loaderData.pick) }) }]
        : [],
    };
  },
  component: MatchPreviewRoute,
});

function MatchPreviewRoute() {
  const search = Route.useSearch();
  const { data } = useSuspenseQuery(summaryQuery(search.home ?? "", search.away ?? ""));
  return (
    <MainLayout>
      <MatchPreview />
      {data && (data.pick || data.recent.home.length || data.recent.away.length) ? (
        <section className="container mx-auto px-4 mt-10 mb-6 rounded-xl border border-border bg-card/40 p-5" aria-label={uiText("Match summary")}>
          <h2 className="text-lg font-semibold mb-2">{search.home}  {uiText("vs")} {search.away}{uiText(": summary")}</h2>
          {data.pick && (
            <p className="text-sm mb-3">
              {uiText(data.pick.market_type)}: {GATES_SUSPENDED ? <strong>{uiText(data.pick.predicted_outcome)}</strong> : <LockedHint variant="view" />}
              {data.pick.odds ? uiText(" · odds {{v0}}", { v0: data.pick.odds.toFixed(2) }) : ""}
              {data.pick.confidence_score != null ? uiText(" · {{v0}}% confidence", { v0: Math.round(data.pick.confidence_score) }) : ""}
              {GATES_SUSPENDED && data.pick.reason ? <span className="block text-xs text-muted-foreground mt-1">{data.pick.reason}</span> : null}
            </p>
          )}
          <div className="grid md:grid-cols-2 gap-4 text-xs">
            {([[search.home, data.recent.home], [search.away, data.recent.away]] as const).map(([name, rows]) => (
              <div key={name}>
                <h3 className="text-sm font-medium mb-1">{name}{uiText(": last")} {rows.length}  {uiText("results")}</h3>
                {rows.length ? (
                  <ul className="space-y-0.5 text-muted-foreground">{rows.map((r) => <li key={r}>{r}</li>)}</ul>
                ) : (
                  <p className="text-muted-foreground">{uiText("No graded results on record yet.")}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </MainLayout>
  );
}
